import { useZero } from "@rocicorp/zero/react";
import { useMemo } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import { useHasAccess } from "../../../hooks/useHasAccess";
import { useIdpSyncLock } from "../../../hooks/useIdpSyncLock";
import { useQuery } from "../../../hooks/useQuery";
import { buildResolveScopeName } from "../../../lib/roleChip";
import { MemberDetailModal } from "./MemberDetailModal";

export function MemberDetailModalById({
  userId,
  onClose,
}: {
  userId: string;
  onClose: () => void;
}) {
  const z = useZero();
  const [members] = useQuery(queries.allUsers());
  const [allSpaces] = useQuery(queries.spaces({ settingsView: true }));
  const [allGroups] = useQuery(queries.allUserGroups());
  const [customRoles] = useQuery(queries.allRolesLite());
  const [allApps] = useQuery(queries.connectorsWithUsages());
  const [allSkills] = useQuery(queries.skillsIndex());
  const [allNetworks] = useQuery(queries.networks());
  const canManageUsers = useHasAccess("tenant:users_manage");
  // Reactivate/deactivate are user-provisioning actions; editing the member's
  // group memberships is a group-membership action, which the IdP only owns when
  // groups are synced — so the two are gated on different concerns.
  const { locked: idpSyncLocked, reason: idpSyncReason } =
    useIdpSyncLock("userProvisioning");
  const { locked: groupMembershipLocked, reason: groupMembershipReason } =
    useIdpSyncLock("groupMembership");

  const member = useMemo(
    () => members.find((m) => m.id === userId),
    [members, userId]
  );

  const personalSpaceId = useMemo(() => {
    for (const space of allSpaces) {
      if (space.mode !== "personal") continue;
      for (const grant of space.roleGrantsAsScope) {
        if (grant.userId === userId) return space.id;
      }
    }
    return undefined;
  }, [allSpaces, userId]);

  const resolveScopeName = useMemo(
    () =>
      buildResolveScopeName(
        allSpaces,
        allApps,
        allGroups,
        allSkills,
        allNetworks
      ),
    [allSpaces, allApps, allGroups, allSkills, allNetworks]
  );

  if (!member) return null;

  return (
    <MemberDetailModal
      member={member}
      groups={allGroups}
      customRoles={customRoles}
      apps={allApps}
      allSpaces={allSpaces}
      allSkills={allSkills}
      resolveScopeName={resolveScopeName}
      personalSpaceId={personalSpaceId}
      canManageUsers={canManageUsers}
      idpSyncLocked={idpSyncLocked}
      idpSyncReason={idpSyncReason}
      groupMembershipLocked={groupMembershipLocked}
      groupMembershipReason={groupMembershipReason}
      onClose={onClose}
      onDeactivate={(m) => {
        z.mutate(sharedMutators.deactivateUser({ userId: m.id }));
      }}
      onReactivate={(m) => {
        z.mutate(sharedMutators.reactivateUser({ userId: m.id }));
      }}
      onRemove={(m) => {
        z.mutate(sharedMutators.deleteInvitedUser({ userId: m.id }));
        onClose();
      }}
    />
  );
}
